import type { HttpErrorResponse } from '@angular/common/http';

import type { TrackingActivity } from '@core/activities-types';
import {
  createEntityCollectionStore,
  DEFAULT_SCOPE_NAME,
  memoizeSelector,
  pipeState,
} from '@core/ngrx-legacy';

import { getStoreScope } from '../../helpers/store-helper';
import type { TrackingActivitiesFiltersParams, TrackingActivitiesStore } from '../../types';
import { ActivitiesDataScope, EntityStoreKey } from '../../types';

type TrackingActivitiesMeta = { scopeMeta: { hasMore: boolean } };

const {
  actions,
  selectors: defaultSelectors,
  reducer,
  namespace,
} = createEntityCollectionStore<TrackingActivity, TrackingActivitiesMeta>()(
  EntityStoreKey.trackingActivities,
  {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => ({
      fetchCollection: (
        state,
        payload: { params: TrackingActivitiesFiltersParams; scope?: string; replace?: boolean },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.resetAll(next) : next),
          next =>
            adapter.markAsPending(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      fetchCollectionSuccess: (
        state,
        payload: {
          data: TrackingActivitiesStore;
          scope?: string;
          replace?: boolean;
          hasMore: boolean;
        },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
          next => adapter.addMany(next, payload.data.activities, { scope: payload.scope }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: payload.data.activities.map(e => e.id),
            }),
          next =>
            adapter.setScopeMetaCustom(
              next,
              { hasMore: payload.hasMore },
              { scope: payload.scope || DEFAULT_SCOPE_NAME },
            ),
        ),

      fetchCandidateTrackingActivities: (
        state,
        payload: {
          params: TrackingActivitiesFiltersParams;
          scope?: string;
          replace?: boolean;
          loadMore?: boolean;
        },
      ) =>
        pipeState(
          state,
          next =>
            payload.replace ? adapter.resetScope(next, payload.scope || DEFAULT_SCOPE_NAME) : next,
          next =>
            adapter.markAsPending(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      fetchCandidateTrackingActivitiesSuccess: (
        state,
        payload: {
          data: TrackingActivitiesStore;
          scope?: string;
          replace?: boolean;
          loadMore?: boolean;
          hasMore: boolean;
        },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
          next =>
            adapter.addMany(next, payload.data.activities, {
              scope: payload.scope,
              prepend: !payload.loadMore,
            }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: payload.data.activities.map(e => e.id),
            }),
          next =>
            adapter.setScopeMetaCustom(
              next,
              { hasMore: payload.hasMore },
              { scope: payload.scope || DEFAULT_SCOPE_NAME },
            ),
        ),
      fetchCandidateTrackingActivitiesFailed: (state, payload: { error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error),

      fetchOfferTrackingActivities: (
        state,
        payload: { params: TrackingActivitiesFiltersParams; scope?: string; replace?: boolean },
      ) =>
        pipeState(
          state,
          next =>
            payload.replace ? adapter.resetScope(next, payload.scope || DEFAULT_SCOPE_NAME) : next,
          next =>
            adapter.markAsPending(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      fetchOfferTrackingActivitiesSuccess: (
        state,
        payload: {
          data: TrackingActivitiesStore;
          scope?: string;
          replace?: boolean;
          hasMore: boolean;
        },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
          next => adapter.addMany(next, payload.data.activities, { scope: payload.scope }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: payload.data.activities.map(e => e.id),
            }),
          next =>
            adapter.setScopeMetaCustom(
              next,
              { hasMore: payload.hasMore },
              { scope: payload.scope || DEFAULT_SCOPE_NAME },
            ),
        ),
      fetchOfferTrackingActivitiesFailed: (state, payload: { error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error),

      fetchTalentPoolTrackingActivities: (
        state,
        payload: { params: TrackingActivitiesFiltersParams; scope?: string; replace?: boolean },
      ) =>
        pipeState(
          state,
          next =>
            payload.replace ? adapter.resetScope(next, payload.scope || DEFAULT_SCOPE_NAME) : next,
          next =>
            adapter.markAsPending(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      fetchTalentPoolTrackingActivitiesSuccess: (
        state,
        payload: {
          data: TrackingActivitiesStore;
          scope?: string;
          replace?: boolean;
          hasMore: boolean;
        },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
          next => adapter.addMany(next, payload.data.activities, { scope: payload.scope }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: payload.data.activities.map(e => e.id),
            }),
          next =>
            adapter.setScopeMetaCustom(
              next,
              { hasMore: payload.hasMore },
              { scope: payload.scope || DEFAULT_SCOPE_NAME },
            ),
        ),
      fetchTalentPoolTrackingActivitiesFailed: (state, payload: { error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error),
      fetchRequisitionTrackingActivities: (
        state,
        payload: { params: TrackingActivitiesFiltersParams; scope?: string; replace?: boolean },
      ) =>
        pipeState(
          state,
          next =>
            payload.replace ? adapter.resetScope(next, payload.scope || DEFAULT_SCOPE_NAME) : next,
          next =>
            adapter.markAsPending(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      fetchRequisitionTrackingActivitiesSuccess: (
        state,
        payload: {
          data: TrackingActivitiesStore;
          scope?: string;
          replace?: boolean;
          hasMore: boolean;
        },
      ) =>
        pipeState(
          state,
          next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
          next => adapter.addMany(next, payload.data.activities, { scope: payload.scope }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload.scope || DEFAULT_SCOPE_NAME,
              entities: payload.data.activities.map(e => e.id),
            }),
          next =>
            adapter.setScopeMetaCustom(
              next,
              { hasMore: payload.hasMore },
              { scope: payload.scope || DEFAULT_SCOPE_NAME },
            ),
        ),
      fetchRequisitionTrackingActivitiesFailed: (state, payload: { error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error),
    }),
  },
);

const selectors = {
  ...defaultSelectors,
  requisitionScopeIsLoaded: memoizeSelector((requisitionId: number) =>
    defaultSelectors.isLoaded({
      scope: getStoreScope(ActivitiesDataScope.Requisition, requisitionId),
    }),
  ),
  requisitionScopeIsPending: memoizeSelector((requisitionId: number) =>
    defaultSelectors.isPending({
      scope: getStoreScope(ActivitiesDataScope.Requisition, requisitionId),
    }),
  ),
};

export {
  actions as TrackingActivitiesActions,
  selectors as TrackingActivitiesSelectors,
  reducer as TrackingActivitiesReducer,
  namespace as TrackingActivitiesNamespace,
};
